import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Wifi, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const OpenerServicePage = () => {
  const openerTypes = [
    { name: "Belt Drive", best: "Quiet attached garages", features: ["Ultra-quiet", "Smooth", "Low maintenance", "Residential favorite"] },
    { name: "Chain Drive", best: "Heavy doors, detached garages", features: ["Durable", "Heavy-duty", "Cold weather reliable", "Cost-effective"] },
    { name: "Screw Drive", best: "Simple, reliable operation", features: ["Few parts", "Reliable", "Minimal maintenance", "Good value"] },
    { name: "Smart Opener", best: "Modern convenience", features: ["Wi-Fi connected", "Smartphone control", "Voice commands", "Alerts"] }
  ];

  const warrantyInfo = [
    "Manufacturer warranty on opener units (varies by brand)",
    "Labor warranty on installation workmanship",
    "Parts warranty on replacement components",
    "Free service call if issues within warranty period",
    "Warranty covers motor, drive system, electronics"
  ];

  const faqItems = [
    {
      q: "How long do garage door openers last in Saskatoon?",
      a: "Quality openers last 10-15 years with proper maintenance. Saskatoon's cold winters can be hard on openers. We recommend belt drive or chain drive openers rated for cold weather operation. Regular maintenance extends opener life. All our installations include manufacturer warranty plus labor warranty."
    },
    {
      q: "Should I choose belt drive or chain drive for my Saskatoon home?",
      a: "Belt drive is quieter - ideal for attached garages or bedrooms near garage. Chain drive is more durable for heavy doors and detached garages. Both work well in Saskatoon winters. For residential homes with living space near garage, we recommend belt drive. For commercial or detached garages, chain drive offers better value."
    },
    {
      q: "Do smart openers work in Saskatoon winters?",
      a: "Yes! Modern smart openers are designed for all climates. Wi-Fi connectivity allows you to monitor and control your door from anywhere - helpful for Saskatoon homeowners who travel. You can check if door is closed, get alerts, and let in service providers remotely. All smart openers include backup battery for power outages."
    },
    {
      q: "What warranty comes with opener installation?",
      a: "Openers include manufacturer warranty (typically 1-5 years depending on brand and model). We provide labor warranty on installation workmanship. Premium models include lifetime motor warranty. All warranties cover parts, labor, and service calls for warranty issues. Commercial installations include commercial-grade warranty coverage."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Garage Door Opener Service Saskatoon</h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Professional opener installation, repair, and replacement for residential and commercial properties in Saskatoon. Smart openers, belt drive, chain drive, and screw drive options with warranty coverage.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg">
                <Phone size={22} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-lg">
                <MessageSquare size={22} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto prose prose-lg max-w-none text-gray-700 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Professional Opener Service in Saskatoon</h2>
            <p>
              Your garage door opener is the convenience center of your garage. At 306 Garage Doors, we install, repair, and maintain all types of garage door openers for Saskatoon residential and commercial properties. From traditional chain drive to modern smart openers with Wi-Fi connectivity, we handle every opener need.
            </p>
            
            <h3 className="text-2xl font-bold text-gray-900 mt-8">Residential Opener Services</h3>
            <p>
              For Saskatoon homeowners, we install and service all major opener brands including LiftMaster, Chamberlain, Genie, and Craftsman. Our residential services include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>New opener installation with smartphone programming</li>
              <li>Opener repair (motor, gears, circuit boards)</li>
              <li>Smart opener upgrades with Wi-Fi connectivity</li>
              <li>Safety sensor installation and alignment</li>
              <li>Remote control programming and replacement</li>
              <li>Backup battery installation for power outages</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8">Commercial Opener Services</h3>
            <p>
              Commercial properties require heavy-duty openers rated for frequent daily use. We service Saskatoon businesses with commercial-grade opener solutions:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Commercial-grade openers for high-cycle operation</li>
              <li>Heavy-duty motors for large sectional doors</li>
              <li>Industrial opener service and maintenance</li>
              <li>High-speed door opener installation</li>
              <li>Access control system integration</li>
              <li>Scheduled maintenance programs</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8">Warranty Coverage</h3>
            <p>
              All opener installations and repairs include comprehensive warranty protection. Manufacturers provide warranties on opener units (typically 1-5 years). We add our labor warranty covering installation workmanship. Premium models include lifetime motor warranties. Saskatoon homeowners and businesses get complete protection.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Opener Types for Saskatoon Properties</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {openerTypes.map((type, idx) => (
                <Card key={idx} className="border-2 border-blue-600">
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-700">{type.name}</CardTitle>
                    <p className="text-gray-600 mt-2">Best for: {type.best}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {type.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Opener Warranty Coverage</h2>
            <Card className="border-2 border-blue-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Shield size={48} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Complete Warranty Protection</h3>
                    <p className="text-gray-700 text-lg mb-4">
                      Every opener installation includes manufacturer warranty plus our labor warranty for Saskatoon customers.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {warrantyInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Opener Service in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            From smart openers to heavy-duty commercial units, 306 Garage Doors provides professional opener service with warranty coverage.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
              <Phone size={24} className="mr-2" />Call {businessInfo.phone}
            </Button>
            <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-8 text-xl">
              Request Free Quote
            </Button>
          </div>
          <p className="mt-6 text-blue-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default OpenerServicePage;